import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Gallery, { ConceptScreen } from "@/concepts/Gallery";
import { ClinicalToday } from "@/concepts/clinical/today";
import { ClinicalWorkbench, ClinicalFocus } from "@/concepts/clinical/workbench";
import { ClinicalParticipant } from "@/concepts/clinical/participant";
import { ClinicalStyles } from "@/concepts/clinical/styles";
import Studies from "@/concepts/clinical/studies";
import StudyBuilder from "@/concepts/clinical/builder";
import Participants from "@/concepts/clinical/participants";
import Administration from "@/concepts/clinical/admin";
import { Shell } from "@/concepts/clinical/kit";

const NotFound = () => (
  <Shell active="none">
    <div className="p-10" data-testid="not-found-page">
      <h1 className="text-[28px] font-semibold tracking-[-0.03em]">Page not found</h1>
      <Link to="/today" className="text-[color:var(--accent-text)] underline" data-testid="not-found-home-link">Return to Today</Link>
    </div>
  </Shell>
);

const LegacyClinical = () => {
  const { screen } = useParams();
  const { search } = useLocation();
  const path = { today: "/today", workbench: "/workbench", focus: "/focus", participant: "/participant", styles: "/styles" }[screen] || "/today";
  return <Navigate to={path + search} replace />;
};

const LegacyStudy = () => {
  const { id } = useParams();
  return <Navigate to={`/workbench?study=${id}`} replace />;
};

const LegacyPerson = () => {
  const { id } = useParams();
  return <Navigate to={`/participant?p=${id}`} replace />;
};

const ThemedToaster = () => {
  const read = () => document.documentElement.getAttribute("data-cm-theme") || "light";
  const [t, setT] = useState(read);
  useEffect(() => {
    const on = () => setT(read());
    window.addEventListener("align-theme", on);
    return () => window.removeEventListener("align-theme", on);
  }, []);
  return <Toaster position="bottom-right" theme={t} richColors closeButton />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/concepts" element={<Gallery />} />
          <Route path="/concepts/clinical/:screen" element={<LegacyClinical />} />
          <Route path="/concepts/:dir/:screen" element={<ConceptScreen />} />
          <Route path="/" element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<ClinicalToday />} />
          <Route path="/studies" element={<Studies />} />
          <Route path="/studies/new" element={<StudyBuilder />} />
          <Route path="/studies/:id/edit" element={<StudyBuilder />} />
          <Route path="/studies/:id" element={<LegacyStudy />} />
          <Route path="/workbench" element={<ClinicalWorkbench />} />
          <Route path="/focus" element={<ClinicalFocus />} />
          <Route path="/participants" element={<Participants />} />
          <Route path="/participants/:id" element={<LegacyPerson />} />
          <Route path="/participant" element={<ClinicalParticipant />} />
          <Route path="/admin" element={<Administration />} />
          <Route path="/styles" element={<ClinicalStyles />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <ThemedToaster />
    </div>
  );
}

export default App;
